import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { getMiniHostAppDetail } from 'mini-host-bridge/lib/getMiniHostAppDetail';
import { getOpxHostDeviceInfo } from 'mini-opx-config/lib/getOpxHostDeviceInfo';
import { isMiniHostAppFeatureEnabled } from 'mini-hostapp-feature-flags/lib/isMiniHostAppFeatureEnabled';
import { isPremiumConsumer } from 'owa-session-store';
import type ObservableOwaUserConfiguration from 'owa-session-store/lib/types/ObservableOwaUserConfiguration';

export const COUNTRY_DEFAULT_SUPPORTED_MARKETS = {
    ae: ['ar-ae', 'en-ae'],
    ar: ['es-ar', 'en-ar'],
    at: ['de-at', 'en-at'],
    au: ['en-au'],
    be: ['fr-be', 'nl-be'],
    br: ['pt-br', 'en-br'],
    ca: ['en-ca', 'fr-ca'],
    ch: ['de-ch', 'fr-ch', 'en-ch'],
    cl: ['es-cl', 'en-cl'],
    cn: ['zh-cn'],
    co: ['es-co', 'en-co'],
    cr: ['es-cr'],
    de: ['de-de', 'en-de'],
    dk: ['da-dk', 'en-dk'],
    do: ['es-do'],
    ec: ['es-ec'],
    eg: ['ar-eg', 'en-eg'],
    es: ['es-es'],
    fi: ['fi-fi', 'en-fi'],
    fr: ['fr-fr'],
    gb: ['en-gb'],
    uk: ['en-gb'],
    gr: ['el-gr', 'en-gr'],
    gt: ['es-gt'],
    hk: ['zh-hk', 'en-hk'],
    id: ['id-id', 'en-id'],
    ie: ['en-ie'],
    in: ['en-in'],
    it: ['it-it'],
    jp: ['ja-jp'],
    kr: ['ko-kr'],
    mx: ['es-mx', 'en-mx'],
    my: ['ms-my', 'en-my'],
    nl: ['nl-nl', 'en-nl'],
    no: ['nb-no', 'en-no'],
    nz: ['en-nz'],
    pe: ['es-pe', 'en-pe'],
    ph: ['fil-ph', 'en-ph'],
    pl: ['pl-pl', 'en-pl'],
    pt: ['pt-pt', 'en-pt'],
    ru: ['ru-ru', 'en-ru'],
    sa: ['ar-sa', 'en-sa'],
    se: ['sv-se', 'en-se'],
    sg: ['en-sg', 'zh-sg'],
    th: ['th-th', 'en-th'],
    tr: ['tr-tr', 'en-tr'],
    tw: ['zh-tw'],
    us: ['en-us', 'es-us'],
    ve: ['es-ve'],
    vn: ['vi-vn', 'en-vn'],
    za: ['en-za'],
};

export function IsPremiumConsumerUser(): boolean {
    return isPremiumConsumer();
}

export function IsChildConsumerUser(): boolean {
    let isChildConsumerUserValue: boolean = false;
    const userConfig = getUserConfiguration();
    if (IsShadowMailboxUser()) {
        isChildConsumerUserValue = calculateAge() < 18;
    } else {
        isChildConsumerUserValue = userConfig?.IsConsumerChild || false;
    }
    return isChildConsumerUserValue;
}

export function IsShadowMailboxUser(): boolean {
    const userConfig = getUserConfiguration();
    return userConfig?.SessionSettings?.IsShadowMailbox;
}

export function getMarketCountryCode(adMarket: string): string {
    return adMarket?.toUpperCase().split('-')[1] || 'US';
}

// If mini is being loaded inside a host, we use this method to get the culture
function getCultureFromHost() {
    let userCulture: string;

    if (isMiniHostAppFeatureEnabled('loadCultureFromHost')) {
        userCulture = getMiniHostAppDetail()?.culture;
    } else {
        // If hosted as an OPX scenario, get culture from host
        const { deviceCulture } = getOpxHostDeviceInfo();
        userCulture = deviceCulture;
    }

    return userCulture;
}

/**
 * Fallback for computing adMarket value from user-culture
 * - For OPX scenarios, returns user-culture as set on the device
 * - For owa-mini, returns user-culture from OwaUserConfiguration
 * @param userConfig
 * @returns string
 */
const getFallbackAdMarket = (userConfig: ObservableOwaUserConfiguration) => {
    const cultureFromHost = getCultureFromHost();

    const userCulture = cultureFromHost || userConfig.SessionSettings.UserCulture;
    const adMarket = userCulture?.includes('-') ? userCulture.toLowerCase() : 'en-us';

    return adMarket;
};

function calculateAdMarketPublisheGroupCode(): string {
    const userConfiguration = getUserConfiguration();
    const usKey: string = 'us';
    let adMarket: string;

    if (userConfiguration?.SessionSettings) {
        // If AdMarket is null which means user does not select any country in the MSA live profile,
        // we will use UserCulture and fall back to en-us if UserCulture is not available as well
        if (
            !userConfiguration.AdMarket ||
            !/^[a-zA-z\-]*$/.test(userConfiguration.AdMarket) ||
            userConfiguration.AdMarket.indexOf('-') === -1
        ) {
            adMarket = getFallbackAdMarket(userConfiguration);
        } else {
            adMarket = userConfiguration.AdMarket.toLowerCase();
        }

        const countryCode = adMarket.split('-')[1];

        // For one user, we first check if his country code is in the supported country list
        // If so and his Ad market matches to the supported page group directly, we will return this adMarket directly
        // If not, we will return the default page group for this market
        if (COUNTRY_DEFAULT_SUPPORTED_MARKETS[countryCode]) {
            if (COUNTRY_DEFAULT_SUPPORTED_MARKETS[countryCode].indexOf(adMarket) > -1) {
                return adMarket;
            } else {
                return COUNTRY_DEFAULT_SUPPORTED_MARKETS[countryCode][0];
            }
        }
    } else {
        // If user config is null, means we are not sure what the user selected and hence we return en-us, since we should not assume an unsupported market
        return COUNTRY_DEFAULT_SUPPORTED_MARKETS[usKey][0];
    }

    // If we reach here, return the ad market directly
    return adMarket;
}

export const globalExports: {
    adMarketPublishGroupCode: string;
    calculateAdMarketPublisheGroupCode: () => string;
} = {
    adMarketPublishGroupCode: undefined,
    calculateAdMarketPublisheGroupCode,
};

export function getAdMarketPublishGroupCode(): string {
    // The native Ad Market will never change during one OWA session.
    // Cache the value so that we do not need to calculate this again and again
    if (typeof globalExports.adMarketPublishGroupCode !== 'undefined') {
        return globalExports.adMarketPublishGroupCode;
    }

    globalExports.adMarketPublishGroupCode = globalExports.calculateAdMarketPublisheGroupCode();

    return globalExports.adMarketPublishGroupCode;
}

function calculateAge(): number {
    // TODO: remove the following ts-expect-error after shared package upgrade
    // @ts-expect-error
    const birthDate = getUserConfiguration()?.SessionSettings?.BirthdayPrecision;

    if (birthDate) {
        const timeDiff = Math.abs(Date.now() - new Date(birthDate).getTime());
        const age = Math.floor(timeDiff / (1000 * 3600 * 24) / 365.25);
        return age;
    } else {
        // If we don't have birthdate, we will return 0 to force the user to be treated as child
        return 0;
    }
}
